import { Routes, Route, Navigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useEffect } from 'react';
import MainLayout from '@components/layout/MainLayout';
import Dashboard from '@pages/Dashboard';
import People from '@pages/People';
import Organisations from '@pages/Organisations';
import Volunteers from '@pages/Volunteers';
import Assets from '@pages/Assets';
import Reports from '@pages/Reports';
import Settings from '@pages/Settings';
import TeamManagement from '@pages/TeamManagement';
import FreeAgent from '@pages/FreeAgent';
import SystemSettings from '@pages/SystemSettings';
import Login from '@pages/Login';
import AccessDenied from '@pages/AccessDenied';
import AccountDeactivated from '@pages/AccountDeactivated';
import { useIdleTimeout } from '@hooks/useIdleTimeout';
import { useTeamMembership } from '@hooks/useTeam';
import { useAuthStore } from '@store/authStore';
import { SessionTimeoutModal } from '@components/common/SessionTimeoutModal';

/**
 * Loading spinner for team membership check
 */
function TeamCheckLoading() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-sca-purple-500 to-sca-pink-500 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="animate-spin w-8 h-8 border-4 border-sca-purple-500 border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-gray-600">Checking access...</p>
      </div>
    </div>
  );
}

/**
 * Protected route that also checks team membership
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const { accounts } = useMsal();
  const { checkMembership, isLoading } = useTeamMembership();
  const { teamStatus, teamRole, setTeamMembership, setUser, showSessionTimeout, setShowSessionTimeout } = useAuthStore();

  // Enable idle timeout for authenticated users
  const { resetTimer } = useIdleTimeout();

  // Handle session timeout modal continue
  const handleSessionContinue = () => {
    setShowSessionTimeout(false);
    resetTimer();
  };

  // Check team membership when authenticated
  useEffect(() => {
    async function checkTeam() {
      if (!isAuthenticated || accounts.length === 0) return;

      // Check if we need to verify membership:
      // - status is 'unknown' (fresh state)
      // - status is 'member' but no role (stale/fallback state that needs refresh)
      const needsCheck = teamStatus === 'unknown' ||
        (teamStatus === 'member' && !teamRole);

      if (!needsCheck) return;

      setTeamMembership('checking');

      const result = await checkMembership();

      if (!result) {
        // API error - default to allowing access for now
        // This prevents lockout if the team API is down
        console.warn('Failed to check team membership, allowing access');
        setTeamMembership('member', null, null);
        return;
      }

      if (result.isMember && result.member) {
        setTeamMembership('member', result.member.role, result.member.id);
        // Also update user info from team membership
        setUser({
          id: result.member.id,
          name: result.member.name,
          email: result.member.email,
          role: result.member.role === 'Admin' ? 'admin' : result.member.role === 'ReadOnly' ? 'readonly' : 'staff',
        });
      } else if (result.reason === 'deactivated') {
        setTeamMembership('deactivated');
      } else {
        setTeamMembership('not_in_team');
      }
    }

    checkTeam();
  }, [isAuthenticated, accounts, teamStatus, teamRole, checkMembership, setTeamMembership, setUser]);

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Still checking team membership
  if (teamStatus === 'unknown' || teamStatus === 'checking' || isLoading) {
    return <TeamCheckLoading />;
  }

  // Not in team - show access denied
  if (teamStatus === 'not_in_team') {
    return <AccessDenied />;
  }

  // Deactivated - show deactivated page
  if (teamStatus === 'deactivated') {
    return <AccountDeactivated />;
  }

  return (
    <>
      {children}
      <SessionTimeoutModal
        isOpen={showSessionTimeout}
        onContinue={handleSessionContinue}
      />
    </>
  );
}

/**
 * Admin-only route wrapper
 */
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { teamRole } = useAuthStore();

  if (teamRole !== 'Admin') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/access-denied" element={<AccessDenied />} />
      <Route path="/account-deactivated" element={<AccountDeactivated />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/people" element={<People />} />
                <Route path="/organisations" element={<Organisations />} />
                <Route path="/volunteers" element={<Volunteers />} />
                <Route path="/assets" element={<Assets />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/finance" element={<FreeAgent />} />
                <Route path="/settings" element={<Settings />} />
                <Route
                  path="/team-management"
                  element={
                    <AdminRoute>
                      <TeamManagement />
                    </AdminRoute>
                  }
                />
                <Route
                  path="/system-settings"
                  element={
                    <AdminRoute>
                      <SystemSettings />
                    </AdminRoute>
                  }
                />
              </Routes>
            </MainLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

export default App;
